import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { isRemovedFromDashpivot } from "../lib/dashpivotRemoval";
import { logError } from "../lib/logError";
import { fetchAllRows } from "../lib/fetchAllRows";
import { isCountedVariationStatus, variationFigures } from "./variationStatsMath";

export interface ProjectVariationStats {
  projectId: string;
  projectName: string;
  contractValue: number;
  approvedVariations: number;
  pendingVariations: number;
  pendingCount: number;
  approvedCount: number;
  totalVariations: number;
  claimsToDate: number;
  varianceTotal: number;
  creditTotal: number;
}

export interface MonthlyVariationData {
  month: string;
  monthNum: number;
  year: number;
  submitted: number;
  approved: number;
  pending: number;
}

export interface VariationsStats {
  projectStats: ProjectVariationStats[];
  monthlyTrend: MonthlyVariationData[];
  totalVariance: number;
  totalCredit: number;
  totalApproved: number;
  totalPending: number;
  // Monthly summary
  thisMonthVariations: number;
  lastMonthVariations: number;
  yearToDateVariations: number;
  thisMonthCount: number;
  lastMonthCount: number;
  yearToDateCount: number;
  thisMonthApproved: number;
  thisMonthWaiting: number;
  lastMonthWaiting: number;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function useVariationsStats() {
  const [stats, setStats] = useState<VariationsStats>({
    projectStats: [],
    monthlyTrend: [],
    totalVariance: 0,
    totalCredit: 0,
    totalApproved: 0,
    totalPending: 0,
    thisMonthVariations: 0,
    lastMonthVariations: 0,
    yearToDateVariations: 0,
    thisMonthCount: 0,
    lastMonthCount: 0,
    yearToDateCount: 0,
    thisMonthApproved: 0,
    thisMonthWaiting: 0,
    lastMonthWaiting: 0,
  });
  const [loading, setLoading] = useState(true);
  // True when any read failed. The dashboard then shows "could not load" instead
  // of figures: a failed read must never pass as a real zero (SEQ-240).
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    // Set by the cleanup so a slow older read can never overwrite a newer one.
    let cancelled = false;
    async function fetchVariationsStats() {
      setLoading(true);
      setError(false);
      try {
        // Fetch active projects with their details
        const { data: projects, error: projectsError } = await supabase
          .from("claims_projects")
          .select("id, job_location, status")
          .or("status.eq.active,status.is.null");
        if (projectsError) throw projectsError;

        if (!projects || projects.length === 0) {
          if (cancelled) return;
          setStats({
            projectStats: [],
            monthlyTrend: [],
            totalVariance: 0,
            totalCredit: 0,
            totalApproved: 0,
            totalPending: 0,
            thisMonthVariations: 0,
            lastMonthVariations: 0,
            yearToDateVariations: 0,
            thisMonthCount: 0,
            lastMonthCount: 0,
            yearToDateCount: 0,
            thisMonthApproved: 0,
            thisMonthWaiting: 0,
            lastMonthWaiting: 0,
          });
          if (!cancelled) setLoading(false);
          return;
        }

        const projectIds = projects.map((p) => p.id);

        // Fetch all variations for these projects. Paged: 754 rows today and
        // growing, past the 1,000-row response cap within months. A failed page
        // throws to the catch below instead of yielding a short list.
        const variations = await fetchAllRows((afterId, limit) => {
          let query = supabase
            .from("claims_variations")
            .select("*")
            .in("project_id", projectIds)
            .order("id", { ascending: true })
            .limit(limit);
          if (afterId) query = query.gt("id", afterId);
          return query;
        });

        // Fetch categories and subcategories for contract values
        const { data: categories, error: categoriesError } = await supabase
          .from("claims_categories")
          .select("*, subcategories:claims_subcategories(*)")
          .in("project_id", projectIds);
        if (categoriesError) throw categoriesError;

        // Fetch claims progress for claims to date. ALL lines, paged: the table
        // holds 1,494 rows and the API returns at most 1,000 per request, so the
        // unpaged select read only part of the money (SEQ-240). The lines that
        // belong to other projects are dropped below by subcatToProject.
        const progress = await fetchAllRows((afterId, limit) => {
          let query = supabase
            .from("claims_progress")
            .select("id, amount, subcategory_id")
            .order("id", { ascending: true })
            .limit(limit);
          if (afterId) query = query.gt("id", afterId);
          return query;
        });

        // Get subcategory to project mapping
        const subcatToProject: Record<string, string> = {};
        (categories || []).forEach((cat) => {
          (cat.subcategories || []).forEach((sub: { id: string }) => {
            subcatToProject[sub.id] = cat.project_id;
          });
        });

        // Calculate per-project stats
        const projectStatsMap: Record<string, ProjectVariationStats> = {};

        // Initialize project stats
        projects.forEach((p) => {
          projectStatsMap[p.id] = {
            projectId: p.id,
            projectName: p.job_location,
            contractValue: 0,
            approvedVariations: 0,
            pendingVariations: 0,
            pendingCount: 0,
            approvedCount: 0,
            totalVariations: 0,
            claimsToDate: 0,
            varianceTotal: 0,
            creditTotal: 0,
          };
        });

        // Calculate contract values
        (categories || []).forEach((cat) => {
          const stats = projectStatsMap[cat.project_id];
          if (!stats) return;

          if (cat.is_custom_amount_only) {
            stats.contractValue += Number(cat.custom_amount || 0);
          } else {
            (cat.subcategories || []).forEach(
              (sub: { amount: number | null }) => {
                stats.contractValue += Number(sub.amount || 0);
              },
            );
          }
        });

        // Calculate claims to date per project
        (progress || []).forEach((p) => {
          const projectId = subcatToProject[p.subcategory_id];
          if (projectId && projectStatsMap[projectId]) {
            projectStatsMap[projectId].claimsToDate += Number(p.amount || 0);
          }
        });

        // Process variations
        let totalVariance = 0;
        let totalCredit = 0;
        let totalApproved = 0;
        let totalPending = 0;
        const monthlyData: Record<string, MonthlyVariationData> = {};

        (variations || []).forEach((v) => {
          // Same gate as the aggregation below and as useDashboardStats: a
          // variation flagged as gone from Dashpivot may be a duplicate of one
          // now living under another project, so it must not be counted twice.
          if (isRemovedFromDashpivot(v)) return;
          // Only valid variations count, as everywhere else in the app
          // (variationStatsMath.ts). Invalid and pending-status ones are left out.
          if (!isCountedVariationStatus(v.status)) return;

          const stats = projectStatsMap[v.project_id];
          if (!stats) return;

          const { sign: multiplier, isCredit, total, approved, waiting } = variationFigures(v);

          // Per-project stats
          stats.totalVariations++;
          if (isCredit) {
            stats.creditTotal += total;
            totalCredit += total;
          } else {
            stats.varianceTotal += total;
            totalVariance += total;
          }

          if (approved > 0) {
            stats.approvedVariations += approved * multiplier;
            stats.approvedCount++;
            totalApproved += approved * multiplier;
          }

          // Waiting is worked out here (total after discount, minus approved),
          // never read from v.waiting_amount, which may not carry the discount.
          if (waiting > 0) {
            stats.pendingVariations += waiting * multiplier;
            stats.pendingCount++;
            totalPending += waiting * multiplier;
          }

          // Monthly trend
          const monthKey = `${v.claim_year}-${v.claim_month}`;
          if (!monthlyData[monthKey]) {
            monthlyData[monthKey] = {
              month: MONTH_NAMES[v.claim_month - 1],
              monthNum: v.claim_month,
              year: v.claim_year,
              submitted: 0,
              approved: 0,
              pending: 0,
            };
          }
          monthlyData[monthKey].submitted += total * multiplier;
          monthlyData[monthKey].approved += approved * multiplier;
          if (waiting > 0) {
            monthlyData[monthKey].pending += waiting * multiplier;
          }
        });

        // Sort monthly data by date
        const monthlyTrend = Object.values(monthlyData).sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.monthNum - b.monthNum;
        });

        // Calculate monthly summary
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();
        const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const lastMonthYear =
          currentMonth === 1 ? currentYear - 1 : currentYear;

        let thisMonthVariations = 0;
        let lastMonthVariations = 0;
        let yearToDateVariations = 0;
        let thisMonthCount = 0;
        let lastMonthCount = 0;
        let yearToDateCount = 0;
        let thisMonthApproved = 0;
        let thisMonthWaiting = 0;
        let lastMonthWaiting = 0;

        (variations || []).forEach((v) => {
          // Matches useDashboardStats: a variation flagged as gone from Dashpivot
          // may be a duplicate of one now living under another project.
          if (isRemovedFromDashpivot(v)) return;
          if (!isCountedVariationStatus(v.status)) return;

          const figures = variationFigures(v);
          const total = figures.total * figures.sign;
          const approved = figures.approved * figures.sign;
          const waiting = figures.waiting * figures.sign;

          // This month
          if (v.claim_month === currentMonth && v.claim_year === currentYear) {
            thisMonthVariations += total;
            thisMonthCount++;
            thisMonthApproved += approved;
            thisMonthWaiting += waiting;
          }

          // Last month
          if (v.claim_month === lastMonth && v.claim_year === lastMonthYear) {
            lastMonthVariations += total;
            lastMonthCount++;
            lastMonthWaiting += waiting;
          }

          // Year to date
          if (v.claim_year === currentYear && v.claim_month <= currentMonth) {
            yearToDateVariations += total;
            yearToDateCount++;
          }
        });

        if (cancelled) return;
        setStats({
          projectStats: Object.values(projectStatsMap).filter(
            (p) => p.totalVariations > 0 || p.contractValue > 0,
          ),
          monthlyTrend,
          totalVariance,
          totalCredit,
          totalApproved,
          totalPending,
          thisMonthVariations,
          lastMonthVariations,
          yearToDateVariations,
          thisMonthCount,
          lastMonthCount,
          yearToDateCount,
          thisMonthApproved,
          thisMonthWaiting,
          lastMonthWaiting,
        });
      } catch (err) {
        logError("Error fetching variations stats:", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchVariationsStats();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return { stats, loading, error, retry };
}
